/**
 * clubCalendar — the Club variant of the "Your swims" month calendar.
 *
 * Same months, same grid, same local-day rule as swimCalendar.ts (it builds the
 * months through buildSwimCalendar); the only addition is each day's SWIMMER
 * count: distinct members with a swim whose local day is that day. The viewer's
 * own swim counts, and a member's two swims on one day count once.
 *
 * Derived, never stored, like clubSessions.ts: re-run on every open.
 */
import { writable } from "svelte/store";
import { buildSwimCalendar, type CalendarMonth } from "./swimCalendar";
import { localDayKey, type ClubSession, type MemberSwim } from "./clubSessions";
import type { ClubSnapshot } from "./club/snapshot";

/**
 * The club's swims as the calendar and the day card count them: the session
 * source's swims (every one of the viewer's, plus each clubmate's latest shared
 * swim) with the C5 snapshot's 14 days of every member's swims added.
 *
 * Ids line up by construction on the native side (ClubSnapshotSender): a
 * clubmate's snapshot `id` is their companion `originId`, a swim's `id` is its
 * workout uuid, and `self` (`__defaultOwner__`) is the viewer, mapped here to the
 * source's viewer id. A swim both sources carry is kept once, the source's copy
 * (it has the richer summary). A snapshot swim has no location type; a pool
 * length marks it pool, anything else stays unknown.
 */
export function mergeClubSwims(swims: MemberSwim[], viewerId: string, snapshot: ClubSnapshot | null): MemberSwim[] {
  if (!snapshot) return swims;
  const seen = new Set(swims.map((ms) => `${ms.memberId}|${ms.swim.uuid}`));
  const merged = [...swims];
  for (const member of snapshot.members) {
    const memberId = member.id === snapshot.self ? viewerId : member.id;
    for (const s of member.swims) {
      const key = `${memberId}|${s.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push({
        memberId,
        swim: {
          uuid: s.id,
          startDate: s.date,
          endDate: s.date,
          activityType: "swimming",
          durationSeconds: s.durationSec,
          totalDistance: { value: s.distanceM, unit: "m" },
          ...((s.poolM ?? 0) > 0 ? { swimmingLocationType: "pool" } : {}),
        },
      });
    }
  }
  return merged;
}

/** The day card's name for a member. The session source names its own companions;
 *  a clubmate the snapshot carries but the companion list does not (a row dropped by
 *  callRows, B-017, or a stale list) used to render its raw CloudKit id. Falls back to
 *  the snapshot's name, and to the id only when neither knows one. */
export function clubLabelFor(labelFor: (memberId: string) => string, snapshot: ClubSnapshot | null) {
  return (memberId: string): string => {
    const label = labelFor(memberId);
    if (label !== memberId || !snapshot) return label;
    const name = snapshot.members.find((m) => m.id === memberId && m.id !== snapshot.self)?.name.trim();
    return name ? name : label;
  };
}

/** "YYYY-MM-DD" for a calendar day's local-midnight Date (clubSessions' key format). */
/** B-025 (Scott 2026-09-13, "Nav follows the tap"): the session a strip bar tap puts in the date
 *  row, the same pick the calendar makes for that day, so the row, the open card and who-was-there
 *  show one day. null for no swims or a day no session covers (the row stays where it is). */
export function sessionForStripTap(sessions: ClubSession[], swims: readonly { date: string }[]): ClubSession | null {
  return swims.length > 0 ? sessionForDay(sessions, new Date(swims[0].date)) : null;
}

function dayKeyOf(date: Date): string {
  return localDayKey(date.toISOString());
}

/** Distinct swimmers per local day key. */
export function swimmersByDay(swims: MemberSwim[]): Map<string, number> {
  const who = new Map<string, Set<string>>();
  for (const ms of swims) {
    if (!Number.isFinite(Date.parse(ms.swim.startDate))) continue;
    const key = localDayKey(ms.swim.startDate);
    const set = who.get(key);
    if (set) set.add(ms.memberId);
    else who.set(key, new Set([ms.memberId]));
  }
  return new Map([...who].map(([key, set]) => [key, set.size]));
}

/** The calendar months for every member's swims, each day carrying `swimmers`. */
export function buildClubCalendar(swims: MemberSwim[], now: Date = new Date()): CalendarMonth[] {
  const months = buildSwimCalendar(
    swims.map((ms) => ms.swim),
    now,
  );
  const counts = swimmersByDay(swims);
  for (const month of months) {
    for (const day of month.days) day.swimmers = counts.get(dayKeyOf(day.date)) ?? 0;
  }
  return months;
}

/** "10 days with swims · 4 with 2+ swimmers": days anyone swam, and days two or
 *  more did. The second half is the grid's legend (VP round 1: three seats could
 *  not tell what the numbers or the bold ones meant), so it names swimmers and
 *  the 2+ threshold in words, never by color. */
export function formatClubMonthSummary(month: CalendarMonth): string {
  const swum = month.days.filter((d) => (d.swimmers ?? 0) > 0).length;
  if (swum === 0) return "No club swims";
  const together = month.days.filter((d) => (d.swimmers ?? 0) >= 2).length;
  return `${swum} ${swum === 1 ? "day" : "days"} with swims · ${together} with 2+ swimmers`;
}

/** The session a calendar day opens: that day's session with the most swimmers,
 *  the earlier one on a tie. null when nobody swam that day. */
export function sessionForDay(sessions: ClubSession[], date: Date): ClubSession | null {
  const key = dayKeyOf(date);
  let best: ClubSession | null = null;
  let bestCount = 0;
  for (const s of sessions) {
    if (localDayKey(s.startDate) !== key) continue;
    const count = new Set(s.entries.map((e) => e.memberId)).size;
    if (count > bestCount || (count === bestCount && best && Date.parse(s.startDate) < Date.parse(best.startDate))) {
      best = s;
      bestCount = count;
    }
  }
  return best;
}

/** Bumped when a day is picked in the Club calendar, so the Club tab brings that
 *  day's card into view (the arrows change the day too, and must not scroll). */
export const clubDayPicked = writable(0);
